import * as Sentry from '@sentry/react';
import axios, {
  AxiosRequestConfig, AxiosResponse, Method
} from 'axios';
import Qs from 'qs';
import { AnyAction, Dispatch } from 'redux';
import { APIConstants } from './constants';


interface IStartedApiAction<T> {
  type: T;
  status: APIConstants.STARTED;
}

interface ISucceededApiAction<T, P = any> {
  type: T;
  status: APIConstants.SUCCEEDED;
  payload: P;
}

interface IFailedApiAction<T> {
  type: T;
  status: APIConstants.FAILED;
  payload: Error;
}

export type ApiAction<T, P = any> =
  | IStartedApiAction<T>
  | ISucceededApiAction<T, P>
  | IFailedApiAction<T>;

function StartedApiAction<T>(type: T): IStartedApiAction<T> {
  return {
    type,
    status: APIConstants.STARTED,
  };
}

function SucceededApiAction<T, P>(
  type: T,
  payload: P,
): ISucceededApiAction<T, P> {
  return {
    type,
    status: APIConstants.SUCCEEDED,
    payload,
  };
}

function FailedApiAction<T>(type: T, error: Error): IFailedApiAction<T> {
  return {
    type,
    status: APIConstants.FAILED,
    payload: error,
  };
}

export interface IApiOptions {
  params?: any;
  paramsSerializer?: boolean;
  data?: any;
  method: Method;
  route: string;
  timeout?: number;
}

export function api<T>(type: T, options: IApiOptions, responseHeadersType?: T, customUrl: boolean = false) {
  return (dispatch: Dispatch<AnyAction>): Promise<any> => {
    dispatch(StartedApiAction(type));

    if (responseHeadersType) {
      dispatch(StartedApiAction(responseHeadersType));
    }

    const apiOptions: AxiosRequestConfig = {
      method: options.method,
      url: customUrl ? options.route : `${process.env.REACT_APP_API_URL || ''}${options.route}`,
      timeout: (1000 * 30),
      timeoutErrorMessage: 'You request is taking longer than usual. Please try again later!',
    };

    if (options.params) {
      apiOptions.params = options.params;
    }

    if (options.paramsSerializer) {
      apiOptions.paramsSerializer = (params) => Qs.stringify(params, { indices: false });
    }

    if (options.data) {
      apiOptions.data = options.data;
    }

    if (options.timeout) {
      apiOptions.timeout = options.timeout;
    }

    return axios(apiOptions)
      .then((resp: AxiosResponse) => {
        if (resp.status === 200) {
          dispatch(SucceededApiAction(type, resp.data));
          if (responseHeadersType) {
            dispatch(SucceededApiAction(responseHeadersType, JSON.parse(resp.headers['x-pagination']) || {}));
          }
          return resp;
        }

        dispatch(FailedApiAction(type, new Error()));
        if (responseHeadersType) {
          dispatch(FailedApiAction(responseHeadersType, new Error()));
        }

        return resp;
      })
      .catch((err) => {
        Sentry.captureException(err);
        dispatch(FailedApiAction(type, err.response));
        const data = err?.response?.data;
        const status = err?.response?.status;
        // eslint-disable-next-line prefer-promise-reject-errors
        return Promise.reject({
          data,
          status,
        });
      });
  };
}
